import React from 'react';
import { Prompt } from '@/hooks/usePrompt';
import { useLanguage } from '@/contexts';
import { LimitGuard } from '@/components/LimitGuard';

interface AboutSectionProps {
  prompt: Prompt;
  totalCopies: number;
  isOwner: boolean;
  isPublic: boolean;
  onToggleVisibility: () => void;
  formatDate: (dateStr: string) => string;
}

const AboutSection: React.FC<AboutSectionProps> = ({
  prompt,
  totalCopies,
  isOwner,
  isPublic,
  onToggleVisibility,
  formatDate,
}) => {
  const { t } = useLanguage();

  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
      <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100 mb-3">{t("about")}</h3>

      <div className="space-y-2 mb-3">
        <div className="flex items-center text-sm">
          <svg className="h-4 w-4 mr-2 text-brand-electric-blue dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <span className="text-gray-600 dark:text-slate-400">{t("views")}:&nbsp;</span>
          <span className="text-green-600 dark:text-green-400 font-medium">
            {prompt.views.toLocaleString()}
          </span>
        </div>

        <div className="flex items-center text-sm">
          <svg className="h-4 w-4 mr-2 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span className="text-gray-600 dark:text-slate-400">{t("copies")}:&nbsp;</span>
          <span className="text-green-600 dark:text-green-400 font-medium">{totalCopies.toLocaleString()}</span>
        </div>
      </div>

      <div className="text-xs text-gray-500 dark:text-slate-400">
        {t("lastUpdated")}: {formatDate(prompt.modified_at)}
      </div>

     
      {isOwner && (
        <div className="flex items-center justify-center gap-3 my-4">
          <label htmlFor="public-toggle" className="text-gray-700 dark:text-slate-300 font-medium">
            {t("visibility")}:
          </label>
     
          {isPublic ? (
            <LimitGuard feature="private_prompts">
              <button
                id="public-toggle"
                onClick={onToggleVisibility}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 bg-brand-electric-blue dark:bg-blue-600`}
                role="switch"
                aria-checked={isPublic}
                aria-label={`Toggle prompt visibility to ${isPublic ? 'private' : 'public'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out translate-x-6`}
                />
              </button>
            </LimitGuard>
          ) : (
            <button
              id="public-toggle"
              onClick={onToggleVisibility}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 bg-gray-300 dark:bg-slate-600`}
              role="switch"
              aria-checked={isPublic}
              aria-label={`Toggle prompt visibility to ${isPublic ? 'private' : 'public'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out translate-x-1`}
              />
            </button>
          )}
          <span className="text-sm text-gray-600 dark:text-slate-300">{isPublic ? t("public") : t("private")}</span>
        </div>
      )}
    </div>
  );
};

export default AboutSection;
